"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import Image from "next/image";
import ImageWrapper from "../ImageWrapper";
import Heading from "../Heading";

export interface DealerSelectableModel {
    _id: string;
    name: string;
    slug: string;
    brand_name: string;
    brand_slug: string;
}

interface DealerModelSelectModalProps {
    showModal: boolean;
    onClose: () => void;
    onSelect?: (model: DealerSelectableModel) => void;
    brandName?: string;
    models?: DealerSelectableModel[];
}

export default function DealerModelSelectModal({
    showModal,
    onClose,
    onSelect,
    brandName = "Selected",
    models = [],
}: DealerModelSelectModalProps) {
    const [isMounted, setIsMounted] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        if (!showModal) {
            setSearch("");
            setSelectedModelId(null);
            return;
        }

        const timer = setTimeout(() => searchInputRef.current?.focus(), 50);
        return () => clearTimeout(timer);
    }, [showModal]);

    const filteredModels = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return models;
        return models.filter((model) => model.name.toLowerCase().includes(query));
    }, [models, search]);

    const handleModelClick = (model: DealerSelectableModel) => {
        setSelectedModelId(model._id);
        onSelect?.(model);
    };

    if (!isMounted || !showModal) return null;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-3xl w-[328px] md:w-[400px] max-w-[95vw] shadow-xl">
                <div className="p-5 md:p-6 relative">
                    <button
                        onClick={onClose}
                        className="absolute top-5 right-5 md:top-6 md:right-6 text-gray-400 hover:text-gray-700 transition-colors"
                        aria-label="Close"
                    >
                        <ImageWrapper
                            src="/popup-close.svg"
                            alt="Close"
                            width={18}
                            height={18}
                            cdnBaseUrl={process.env.NEXT_PUBLIC_CDN_STATIC_URL}
                        />
                    </button>

                    <Heading tag="p" className="mb-3 !text-[16px] !md:text-[18px] font-semibold text-gray-900">
                        Select {brandName} Models
                    </Heading>

                    <div className="relative mb-4">
                        <Image
                            src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/search.svg`}
                            alt=""
                            width={18}
                            height={18}
                            className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] invert-[60%] brightness-[70%] pointer-events-none"
                        />
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search Models..."
                            className="w-full pl-10 pr-4 py-[12px] border border-gray-200 rounded-xl text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#008C8B]/30 focus:border-[#008C8B]"
                        />
                    </div>

                    <div className="max-h-[280px] overflow-y-auto pr-1 -mr-1">
                        {filteredModels.length > 0 ? (
                            filteredModels.map((model) => {
                                const isSelected = selectedModelId === model._id;

                                return (
                                    <button
                                        key={model._id}
                                        type="button"
                                        onClick={() => handleModelClick(model)}
                                        className={`w-full text-left px-3 py-[8px] md:py-3 rounded-xl text-[15px] font-medium text-gray-800 transition-colors cursor-pointer ${
                                            isSelected
                                                ? "bg-[#E6F4F1]"
                                                : "hover:bg-[#E6F4F1]"
                                        }`}
                                    >
                                        {model.name}
                                    </button>
                                );
                            })
                        ) : (
                            <p className="text-center text-gray-500 py-8 text-sm">No models found</p>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
